import React, { useState } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { MapPin, Phone, Target, Image as ImageIcon, Mail, Globe, ChevronLeft, ChevronRight, X, MessageSquare } from 'lucide-react';
import { companyInfo, socialMedia, getCategoriesByLanguage } from '../mock/data';
import QuoteForm from './QuoteForm';
import { useLanguage } from '../context/LanguageContext';

const Header = () => {
  const [activeTab, setActiveTab] = useState('mision');
  const [selectedCategory, setSelectedCategory] = useState('bodas');
  const { language, changeLanguage, t } = useLanguage();
  const categories = getCategoriesByLanguage(language);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);

  const handlePreviousImage = () => {
    if (selectedImage && selectedImage.categoryMedia) {
      const currentIndex = selectedImage.index;
      const newIndex = currentIndex === 0 ? selectedImage.categoryMedia.length - 1 : currentIndex - 1;
      setSelectedImage({
        ...selectedImage.categoryMedia[newIndex],
        index: newIndex,
        categoryMedia: selectedImage.categoryMedia
      });
    }
  };

  const handleNextImage = () => {
    if (selectedImage && selectedImage.categoryMedia) {
      const currentIndex = selectedImage.index;
      const newIndex = currentIndex === selectedImage.categoryMedia.length - 1 ? 0 : currentIndex + 1;
      setSelectedImage({
        ...selectedImage.categoryMedia[newIndex],
        index: newIndex,
        categoryMedia: selectedImage.categoryMedia
      });
    }
  };

  return (
    <div className="page-wrapper">
      <div className="language-banner">
        <div className="language-selector">
          <Globe className="language-icon" />
          <select 
            value={language} 
            onChange={(e) => changeLanguage(e.target.value)}
            className="language-dropdown"
          >
            <option value="en">English</option>
            <option value="es">Español</option>
          </select>
        </div>
      </div>
      
      <header className="header-container-top">
        <div className="mini-logo">
          <img 
            src="https://customer-assets.emergentagent.com/job_event-showcase-52/artifacts/oo0ijopu_IMG_4034.png" 
            alt="Calypso Spiral" 
            className="mini-logo-img"
          />
          <img 
            src="https://customer-assets.emergentagent.com/job_event-showcase-52/artifacts/ptetdyue_20250424030849.png" 
            alt="AZ" 
            className="mini-logo-img"
          />
        </div>
        <Tabs value={activeTab} onValueChange={setActiveTab} className="main-tabs">
          <TabsList className="tabs-list">
            <TabsTrigger value="mision" className="tab-trigger">
              <Target className="tab-icon" />
              {t('mission')}
            </TabsTrigger>
            <TabsTrigger value="galeria" className="tab-trigger">
              <ImageIcon className="tab-icon" />
              {t('gallery')}
            </TabsTrigger>
            <TabsTrigger value="ubicacion" className="tab-trigger">
              <MapPin className="tab-icon" />
              {t('location')}
            </TabsTrigger>
            <TabsTrigger value="contacto" className="tab-trigger">
              <Phone className="tab-icon" />
              {t('contact')}
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <button 
          className="quote-button-header"
          onClick={() => setQuoteModalOpen(true)}
          data-testid="header-quote-button"
        >
          <MessageSquare className="tab-icon" />
        </button>
      </header>

      <main className="main-content">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsContent value="mision" className="content-section">
            <div className="content-wrapper">
              <Target className="section-icon" />
              <h2 className="section-title">{t('missionTitle')}</h2>
              <p className="section-text">{t('missionText')}</p>
            </div>
          </TabsContent>

          <TabsContent value="galeria" className="content-section">
            <div className="content-wrapper">
              <div className="gallery-tabs">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`gallery-tab-button ${selectedCategory === category.id ? 'active' : ''}`}
                    style={{
                      borderColor: selectedCategory === category.id ? category.color : 'transparent',
                      color: selectedCategory === category.id ? category.color : '#666'
                    }}
                  >
                    {category.name}
                  </button>
                ))}
              </div>

              <div className="gallery-content">
                {categories
                  .filter(cat => cat.id === selectedCategory)
                  .map(category => (
                    <div key={category.id} className="gallery-section">
                      <div className="gallery-grid-center">
                        {category.media.map((item, index) => (
                          <div 
                            key={index}
                            className="gallery-item-center"
                            style={{ borderColor: category.color }}
                            onClick={() => {
                              setSelectedImage({ ...item, index, categoryMedia: category.media });
                              setImageModalOpen(true);
                            }}
                          >
                            <img
                              src={item.url}
                              alt={item.title}
                              className="gallery-image-center"
                            />
                          </div>
                        ))}
                      </div>
                      <div className="gallery-cta-center">
                        <QuoteForm 
                          eventType={category.name}
                          buttonColor={category.color}
                        />
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </TabsContent>

          <TabsContent value="ubicacion" className="content-section">
            <div className="content-wrapper">
              <div className="location-details">
                <div className="map-container">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d98140.55621913513!2d-83.14780654999999!3d39.983334449999995!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x883889c1b990de71%3A0xe43266f8cfb1b533!2sColumbus%2C%20OH!5e0!3m2!1sen!2sus!4v1704394800000!5m2!1sen!2sus&z=11"
                    width="100%"
                    height="500"
                    style={{ border: 0, borderRadius: '12px' }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="Columbus, Ohio - Highway 270 Area"
                  ></iframe>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="contacto" className="content-section">
            <div className="content-wrapper">
              <Phone className="section-icon" />
              <h2 className="section-title">{t('contactTitle')}</h2>
              <p className="section-subtitle">{t('contactSubtitle')}</p>
              <div className="contact-grid-vertical">
                {socialMedia
                  .filter(social => !['Email', 'Teléfono', 'Phone'].includes(social.name))
                  .map((social) => {
                    const IconComponent = require('lucide-react')[social.icon];
                    const translatedName = t(social.name.toLowerCase()) || social.name;
                    return (
                      <a
                        key={social.name}
                        href={social.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="social-link-vertical"
                        style={{ borderColor: social.color }}
                      >
                        <IconComponent className="social-icon-vertical" style={{ color: social.color }} />
                        <span className="social-name-vertical">{translatedName}</span>
                      </a>
                    );
                  })}
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </main>

      {/* Image Navigation Modal */}
      {imageModalOpen && selectedImage && (
        <div 
          className="image-viewer-modal"
          onClick={(e) => {
            if (e.target.className === 'image-viewer-modal') {
              setImageModalOpen(false);
            }
          }}
        >
          <button 
            onClick={() => setImageModalOpen(false)}
            className="modal-close-button"
          >
            <X />
          </button>
          
          <button 
            onClick={handlePreviousImage}
            className="nav-button nav-button-left"
          >
            <ChevronLeft />
          </button>
          
          <img
            src={selectedImage.url}
            alt="Gallery image"
            className="viewer-image"
          />
          
          <button 
            onClick={handleNextImage}
            className="nav-button nav-button-right"
          >
            <ChevronRight />
          </button>
          
          <div className="image-counter">
            {selectedImage.index + 1} / {selectedImage.categoryMedia?.length || 0}
          </div>
        </div>
      )}

      {/* Quote Modal from Header */}
      {quoteModalOpen && (
        <div 
          className="quote-modal-overlay"
          onClick={(e) => {
            if (e.target.className === 'quote-modal-overlay') {
              setQuoteModalOpen(false);
            }
          }}
        >
          <div className="quote-modal-header">
            <button 
              className="modal-close-button-quote"
              onClick={() => setQuoteModalOpen(false)}
            >
              <X />
            </button>
            <QuoteForm 
              eventType={t('generalInquiry')}
              buttonColor="#ff84e4"
              isHeaderModal={true}
              onClose={() => setQuoteModalOpen(false)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Header;
